import type { SessionScreen } from './session';
import type { ScreenQuestion, ShowIfCondition } from './types';

export function cleanTitle(title: string): string {
  return title
    .replace(
      /^(Welcome & Setup|Onboarding|Year & Residency|Consent & Access|My Info|Income(?!\s+by\s)(?!\s+from\s)(?!\s+Details)|Deductions|Foreign Reporting|Non-Resident|Payments|Review & Submit|Documents|Support|Profile)\s*/gi,
      ''
    )
    .replace(/\s+/g, ' ')
    .trim();
}

export function cleanParagraph(text: string): string {
  return text
    .replace(
      /^(Welcome & Setup|Onboarding|Year & Residency|Consent & Access|My Info|Income|Deductions|Foreign Reporting|Non-Resident|Payments|Review & Submit|Documents|Support|Profile)\s*/gi,
      ''
    )
    .replace(/\s+/g, ' ')
    .trim();
}

export function evaluateShowIf(condition: ShowIfCondition | undefined | any, answers: Record<string, any>): boolean {
  if (!condition) return true;

  if (condition.field && !condition.op) {
    const actual = answers[condition.field];
    if (condition.includes !== undefined) {
      if (Array.isArray(actual)) return actual.includes(condition.includes);
      return false;
    }
    if (condition.value !== undefined) {
      return actual === condition.value;
    }
    return true;
  }

  const { field, op, value, conditions, condition: nestedCondition, includes, values } = condition;

  switch (op) {
    case 'and':
      return (conditions || []).every((c: ShowIfCondition) => evaluateShowIf(c, answers));
    case 'or':
      return (conditions || []).some((c: ShowIfCondition) => evaluateShowIf(c, answers));
    case 'not':
      return !evaluateShowIf(nestedCondition, answers);
  }

  if (!field) return true;
  const actual = answers[field];
  // Some flows (e.g. non-Spain/US sessions) may not populate `region` at all, but
  // the FIGMA show_if rules still reference it. If the value is missing/empty,
  // don't hide the question set entirely — treat it as visible.
  if (field === 'region' && (actual === undefined || actual === null || actual === '')) {
    return true;
  }

  switch (op) {
    case 'eq': {
      if (actual === value) return true;
      if (actual === undefined || actual === null) return false;
      return String(actual) === String(value);
    }
    case 'neq': {
      if (actual === value) return false;
      if (actual === undefined || actual === null) return true;
      return String(actual) !== String(value);
    }
    case 'gt':
      return actual !== undefined && actual !== null && Number(actual) > value;
    case 'lt':
      return actual !== undefined && actual !== null && Number(actual) < value;
    case 'gte':
      return actual !== undefined && actual !== null && Number(actual) >= value;
    case 'lte':
      return actual !== undefined && actual !== null && Number(actual) <= value;
    case 'in':
      return Array.isArray(values) ? values.includes(actual) : Array.isArray(value) && value.includes(actual);
    case 'contains':
      if (Array.isArray(actual)) return actual.includes(value);
      return typeof actual === 'string' && actual.includes(String(value));
    case 'includes':
      if (Array.isArray(actual)) return actual.includes(includes ?? value);
      return false;
    case 'exists':
      return actual !== undefined && actual !== null && actual !== '';
    case 'not_exists':
      return actual === undefined || actual === null || actual === '';
    default:
      return true;
  }
}

export function isNonInputQuestionFieldType(fieldType: string | undefined): boolean {
  if (!fieldType) return false;
  return [
    'section_header',
    'add_item',
    'review_display',
    'review_files',
    'tax_summary_section',
    'tax_calculated',
    'tax_breakdown',
    'tax_total',
  ].includes(fieldType);
}

export function isQuestionLayoutHalf(layout: string | undefined | null): boolean {
  if (layout == null || layout === '') return false;
  return String(layout).trim().toLowerCase() === 'half';
}

export function isQuestionLayoutWideLabel(layout: string | undefined | null): boolean {
  if (layout == null || layout === '') return false;
  return String(layout).trim().toLowerCase() === 'wide-label';
}

/** Half/full only applies at lg+; smaller viewports use a single-column grid (full width). */
export function questionGridCellClass(layout: string | undefined | null): string {
  const half = isQuestionLayoutHalf(layout);
  return ['min-w-0', 'col-span-1', half ? 'lg:col-span-1' : 'lg:col-span-2'].join(' ');
}

export function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function extractTemplateIndicesFromIds(ids: string[], template: string): number[] {
  const re = new RegExp(`^${escapeRegExp(template)}_(\\d+)_`);
  const out = new Set<number>();
  for (const id of ids) {
    const m = String(id).match(re);
    if (m) {
      const n = parseInt(m[1], 10);
      if (!Number.isNaN(n)) out.add(n);
    }
  }
  return Array.from(out);
}

export function extractTemplateIndicesFromKeys(keys: string[], template: string): number[] {
  return extractTemplateIndicesFromIds(keys, template);
}

export function mergeTemplateSectionIndicesFromAnswersAndQuestions(
  answerKeys: string[],
  questionIds: string[],
  template: string
): number[] {
  const fromKeys = extractTemplateIndicesFromKeys(answerKeys, template);
  const fromQuestions = extractTemplateIndicesFromIds(questionIds, template);
  return Array.from(new Set([...fromKeys, ...fromQuestions])).sort((a, b) => a - b);
}

export function reindexAnswersAfterDeletingTemplateSection(
  prev: Record<string, unknown>,
  template: string,
  delIdx: number,
  logicalIndices: number[]
): Record<string, unknown> {
  const re = new RegExp(`^${escapeRegExp(template)}_(\\d+)_(.*)$`);
  const sorted = Array.from(new Set(logicalIndices)).filter((n) => !Number.isNaN(n)).sort((a, b) => a - b);
  if (sorted.length === 0 || !sorted.includes(delIdx)) {
    return prev;
  }

  const remaining = sorted.filter((i) => i !== delIdx);
  const oldToNew = new Map<number, number>();
  remaining.forEach((old, pos) => oldToNew.set(old, pos + 1));

  const next: Record<string, unknown> = { ...prev };
  for (const k of Object.keys(next)) {
    if (k.match(re)) delete next[k];
  }

  for (const [key, val] of Object.entries(prev)) {
    const m = key.match(re);
    if (!m) continue;
    const oldIdx = parseInt(m[1], 10);
    const rest = m[2];
    if (oldIdx === delIdx) continue;
    const newIdx = oldToNew.get(oldIdx);
    if (newIdx === undefined) continue;
    next[`${template}_${newIdx}_${rest}`] = val;
  }

  return next;
}

export function defaultAnswerForFieldType(fieldType: string | undefined): unknown {
  const ft = String(fieldType ?? '').trim();
  switch (ft) {
    case 'checkbox':
      return [];
    case 'file':
      return null;
    case 'number':
    case 'currency':
    case 'date':
    case 'radio':
    case 'select':
    case 'text':
      return '';
    default:
      return '';
  }
}

/** Rewrite `field` references inside a show_if tree from oldPrefix → newPrefix. */
function rewriteShowIfFields(condition: any, oldPrefix: string, newPrefix: string): any {
  if (!condition || typeof condition !== 'object') return condition;
  const result = { ...condition };
  if (typeof result.field === 'string' && result.field.startsWith(oldPrefix)) {
    result.field = newPrefix + result.field.slice(oldPrefix.length);
  }
  if (Array.isArray(result.conditions)) {
    result.conditions = result.conditions.map((c: any) => rewriteShowIfFields(c, oldPrefix, newPrefix));
  }
  if (result.condition) {
    result.condition = rewriteShowIfFields(result.condition, oldPrefix, newPrefix);
  }
  return result;
}

export function buildDisplayQuestionsForClientSideAddItems(
  rawQuestions: ScreenQuestion[],
  answers: Record<string, unknown>
): ScreenQuestion[] {
  if (!rawQuestions.length) return rawQuestions;

  let result = [...rawQuestions];

  const addItems = rawQuestions.filter((q) => q.field_type === 'add_item' && q.item_template);
  if (addItems.length === 0) return result;

  for (const addItem of addItems) {
    const template = addItem.item_template!;

    const baseIndices = extractTemplateIndicesFromIds(
      rawQuestions.map((q) => q.id),
      template
    );
    if (baseIndices.length === 0) continue;

    const existingIndices = extractTemplateIndicesFromIds(
      result.map((q) => q.id),
      template
    );
    const indicesInAnswers = extractTemplateIndicesFromKeys(Object.keys(answers), template);

    const existingSet = new Set<number>(existingIndices);
    const indicesToAdd = indicesInAnswers.filter((n) => !existingSet.has(n)).sort((a, b) => a - b);
    if (indicesToAdd.length === 0) continue;

    const baseIdx = Math.min(...baseIndices);
    const basePrefix = `${template}_${baseIdx}_`;
    const baseBlock = rawQuestions.filter((q) => String(q.id).startsWith(basePrefix));
    if (!baseBlock.length) continue;

    const reAnyBlock = new RegExp(`^${escapeRegExp(template)}_\\d+_`);
    let insertPos = -1;
    for (let i = 0; i < result.length; i++) {
      if (reAnyBlock.test(result[i].id)) insertPos = i;
    }
    const before = insertPos >= 0 ? result.slice(0, insertPos + 1) : result;
    const after = insertPos >= 0 ? result.slice(insertPos + 1) : [];

    const newBlocks: ScreenQuestion[] = [];
    for (const newIdx of indicesToAdd) {
      const newPrefix = `${template}_${newIdx}_`;
      for (const q of baseBlock) {
        const newId = String(q.id).startsWith(basePrefix)
          ? String(q.id).replace(basePrefix, newPrefix)
          : String(q.id);

        let newText = q.question_text;
        if (q.field_type === 'section_header' && String(q.question_text).includes(String(baseIdx))) {
          newText = String(q.question_text).split(String(baseIdx)).join(String(newIdx));
        }

        newBlocks.push({
          ...q,
          id: newId,
          question_text: newText,
          show_if: q.show_if ? rewriteShowIfFields(q.show_if, basePrefix, newPrefix) : q.show_if,
        });
      }
    }

    result = [...before, ...newBlocks, ...after];
  }

  return result;
}

export function isRequiredQuestionAnswerSatisfied(q: ScreenQuestion, value: unknown): boolean {
  if (!q.required) return true;
  switch (q.field_type) {
    case 'checkbox': {
      const arr = Array.isArray(value) ? value : [];
      return arr.length > 0;
    }
    case 'radio':
    case 'select':
      return value !== undefined && value !== null && String(value).trim() !== '';
    case 'number':
      if (value === '' || value === null || value === undefined) return false;
      return !Number.isNaN(typeof value === 'number' ? value : Number(value));
    case 'currency':
      if (value === '' || value === null || value === undefined) return false;
      return !Number.isNaN(typeof value === 'number' ? value : Number(value));
    case 'date':
    case 'text':
      return typeof value === 'string' && value.trim() !== '';
    case 'file':
    case 'file_upload':
      if (value instanceof File) return true;
      return value !== null && value !== undefined && value !== '';
    default:
      if (isNonInputQuestionFieldType(q.field_type)) return true;
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim() !== '';
      return true;
  }
}

export function normalizeIllustrationAssetName(screenId: string): string {
  return screenId.trim().toLowerCase().replace(/_/g, '-');
}

export function getIllustrationSrc(screen: SessionScreen): string {
  return `/img/${normalizeIllustrationAssetName(screen.screen_id)}.png`;
}

export function getForwardButtonLabel(
  screen: SessionScreen,
  t?: (key: string) => string
): string {
  if (!screen.navigation.can_go_back) {
    return t ? t('common:buttons.lets_get_started') : "Let's Get Started";
  }
  const bs = screen.buttons ?? [];
  const forward = bs.filter((b) => !/^back$/i.test(String(b).trim()));
  if (forward.length > 0) return forward[forward.length - 1];
  return t ? t('common:buttons.continue') : 'Continue';
}

/** Fallback when no tax year is in session storage and API omits it.
 *  Returns previous year — users file taxes for the year just ended, not the current one. */
export function getDefaultTaxYear(): number {
  return new Date().getFullYear() - 1;
}

export const CO_OWNERSHIP_DETAIL_SCREEN_IDS = new Set([
  'co_ownership_details',
  'co_ownership_details_property',
  'co_ownership_details_rental',
  'co_ownership_details_sold',
  'co_ownership_details_spain_income',
]);

/**
 * Returns { userPct, coownerTotal, remaining } for co-ownership screens.
 * `remaining` is how much % is still unaccounted for by co-owners.
 */
export function calcCoOwnershipPercentages(answers: Record<string, unknown>): {
  userPct: number;
  coownerTotal: number;
  remaining: number;
} {
  const userPct = typeof answers.coownership_context_user_pct === 'number'
    ? answers.coownership_context_user_pct
    : 0;
  const coownerTotal = Object.entries(answers)
    .filter(([k]) => /^coowner_\d+_percentage$/.test(k))
    .reduce((sum, [, v]) => {
      const n = typeof v === 'number' ? v : typeof v === 'string' ? parseFloat(v) : NaN;
      return sum + (Number.isNaN(n) ? 0 : n);
    }, 0);
  return { userPct, coownerTotal, remaining: 100 - userPct - coownerTotal };
}

export const DATA_USE_CONSENT_SCREEN_ID = 'data_use_consent';
export const NON_RESIDENT_REVIEW_SCREEN_ID = 'non_resident_review_form_210';

export const NON_RESIDENT_TAX_PAYMENT_IBAN_SCREEN_ID = 'non_resident_tax_payment_iban';

export const NON_RESIDENT_PAYMENT_SCREEN_ID = 'non_resident_payment';

/** Copy from FIGMA when the flow API omits `help_text` on questions. */
export const AEAT_IBAN_HELP_TEXT =
  'This is the IBAN of the bank account you want to use to pay your tax. Please enter the IBAN in the format shown above.';

/** Resolve help copy from payload quirks (`help_text` vs `helpText`) and known fallbacks. */
export function effectiveQuestionHelpText(q: ScreenQuestion): string {
  const anyQ = q as ScreenQuestion & { helpText?: string };
  const raw =
    (typeof anyQ.help_text === 'string' ? anyQ.help_text : '') ||
    (typeof anyQ.helpText === 'string' ? anyQ.helpText : '');
  const t = raw.trim();
  if (t) return t;
  if (q.id === 'aeat_iban') return AEAT_IBAN_HELP_TEXT;
  return '';
}

export const DATA_USE_CONSENT_TAGLINE_DEFAULT =
  'Your data is used solely to prepare and file your tax return. We will never sell or share it with third parties.';

export const DATA_USE_CONSENT_CHECKBOX_LABEL =
  'I consent to InnoTaxes processing my personal data to prepare and file my tax return';

export function parseDataUseConsentParagraphs(paragraphs: string[]): {
  tagline: string;
  bodyParagraphs: string[];
} {
  let tagline = DATA_USE_CONSENT_TAGLINE_DEFAULT;
  const body: string[] = [];

  for (const raw of paragraphs) {
    const line = cleanParagraph(raw);
    if (!line) continue;
    const compact = line.replace(/\s+/g, ' ');
    if (/back\s+.*continue/i.test(compact)) continue;
    if (
      /yes,\s*i agree to share my data/i.test(line) ||
      /i consent to innotaxes processing my personal data/i.test(line)
    ) {
      continue;
    }
    if (
      /^sí,\s*acepto compartir mis datos/i.test(line) ||
      /^consiento que innotaxes trate mis datos personales/i.test(line)
    ) {
      continue;
    }
    if (/^welcome & setup.*data use & consent$/i.test(compact) && compact.length < 160) {
      continue;
    }
    if (
      /only use your data to prepare and file your taxes/i.test(line) ||
      /your data is used solely to prepare and file your tax return/i.test(line) ||
      /solo utilizaremos tus datos para preparar y presentar tu declaración/i.test(line) ||
      /tus datos se utilizan únicamente para preparar y presentar tu declaración/i.test(line)
    ) {
      tagline = line;
      continue;
    }
    body.push(line);
  }

  return { tagline, bodyParagraphs: body };
}

export function prettyAnswerKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

export function isMetaAnswerKey(key: string): boolean {
  return (
    key === 'country' ||
    key === 'tax_year' ||
    key === 'region' ||
    key === 'postal_code' ||
    key === 'property_ownership_percentage' ||
    key === 'rental_ownership_percentage' ||
    key === 'spain_income_ownership_percentage' ||
    key === 'spain_situations' ||
    key.startsWith('coownership_context_') ||
    /^sold_\d+_withholding_3percent$/.test(key)
  );
}

export function sectionForAnswerKey(key: string): string {
  if (/^(first_name|middle_name|second_surname|first_surname|date_of_birth|taxpayer_age)$/.test(key)) {
    return 'Your data';
  }
  if (/^spain_/.test(key) || /^(region|postal_code)$/.test(key)) {
    return 'Tax ID & Address';
  }
  if (/^property_\d+_/.test(key) || /^property_/.test(key)) {
    return 'Property in Spain for personal use';
  }
  if (/^rental_\d+_/.test(key) || /^rental_/.test(key)) {
    return 'Rental property';
  }
  if (/^sold_\d+_/.test(key) || /^sold_/.test(key)) {
    return 'Sold property';
  }
  if (/^spain_income_\d+_/.test(key) || /^spain_income_/.test(key)) {
    return 'Spanish-source income';
  }
  return 'Other';
}

export type UploadedFileRow = {
  sectionName: string;
  answerKey: string;
  fileIndex: number;
  fileName: string;
};

export type FileRef = { kind: 'file'; name: string };

export function isFileRef(v: unknown): v is FileRef {
  return (
    typeof v === 'object' &&
    v !== null &&
    (v as any).kind === 'file' &&
    typeof (v as any).name === 'string' &&
    (v as any).name.trim() !== ''
  );
}

export function isPngFileName(name: string): boolean {
  return name.trim().toLowerCase().endsWith('.png');
}

/** PDF and common image types allowed for ID / tax-return uploads. */
export function isAllowedDocumentUploadFileName(name: string): boolean {
  const lower = name.trim().toLowerCase();
  return (
    lower.endsWith('.pdf') ||
    lower.endsWith('.png') ||
    lower.endsWith('.jpg') ||
    lower.endsWith('.jpeg')
  );
}

function isLikelyFileAnswerKey(key: string): boolean {
  const k = key.toLowerCase();
  return k.includes('upload') || k.includes('file');
}

export function normalizeFileAnswerValue(v: unknown): FileRef[] {
  if (isFileRef(v)) return [v];
  if (Array.isArray(v)) {
    const out: FileRef[] = [];
    for (const item of v) {
      if (isFileRef(item)) out.push(item);
    }
    return out;
  }
  return [];
}

export function normalizeFileAnswerValueForKey(key: string, v: unknown): FileRef[] {
  const base = normalizeFileAnswerValue(v);
  if (base.length > 0) return base;
  if (!isLikelyFileAnswerKey(key)) return [];
  if (typeof v === 'string' && v.trim()) return [{ kind: 'file' as const, name: v.trim() }];
  if (Array.isArray(v)) {
    return v
      .filter((x) => typeof x === 'string' && String(x).trim() !== '')
      .map((x) => ({ kind: 'file' as const, name: String(x).trim() }));
  }
  return [];
}

export function uploadedFilesFromAnswers(answers: Record<string, unknown>): UploadedFileRow[] {
  const out: UploadedFileRow[] = [];
  for (const [k, v] of Object.entries(answers)) {
    const refs = normalizeFileAnswerValueForKey(k, v).filter((r) => isPngFileName(r.name));
    if (refs.length === 0) continue;
    refs.forEach((ref, idx) => {
      out.push({ sectionName: sectionForAnswerKey(k), answerKey: k, fileIndex: idx, fileName: ref.name });
    });
  }
  return out;
}

export function cleanQuestionText(questionText: string, cleanedScreenTitle: string): string {
  const raw = (questionText ?? '').trim();
  if (!raw) return raw;

  if (cleanedScreenTitle) {
    const rawLower = raw.toLowerCase();
    const titleLower = cleanedScreenTitle.toLowerCase();
    if (rawLower.startsWith(titleLower)) {
      return raw.slice(cleanedScreenTitle.length).trim();
    }
  }

  return cleanParagraph(raw);
}

// ---------------------------------------------------------------------------
// `user_identity_intake` — same NIF/NIE rules as `app/integrations/aeat/nif_utils.py`
// ---------------------------------------------------------------------------

const _IDENTITY_NIF_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';
const _IDENTITY_DNI_RE = /^(\d{8})([A-Z])$/;
const _IDENTITY_NIE_RE = /^([XYZ])(\d{7})([A-Z])$/;
const _IDENTITY_NIE_PREFIX_MAP: Record<string, string> = { X: '0', Y: '1', Z: '2' };

/** i18n keys under `common` namespace */
export const IDENTITY_I18N = {
  fullNameMin: 'validation.identity_full_name_min',
  emailRequired: 'validation.required',
  emailInvalid: 'validation.invalid_email',
  nifRequired: 'validation.required',
  nifFormat: 'validation.identity_nif_format',
  nifCheckLetter: 'validation.identity_nif_check_letter',
} as const;

export type IdentityIntakeField = 'full_name' | 'email' | 'nif';

export function isValidEmail(value: string): boolean {
  const t = value.trim();
  if (!t) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t) && t.length <= 254;
}

export function validateNifNie(
  raw: string
):
  | { ok: true; normalized: string }
  | { ok: false; messageKey: typeof IDENTITY_I18N.nifFormat | typeof IDENTITY_I18N.nifCheckLetter } {
  const nif = raw.trim().toUpperCase();
  if (!nif) {
    return { ok: false, messageKey: IDENTITY_I18N.nifFormat };
  }

  const mDni = _IDENTITY_DNI_RE.exec(nif);
  if (mDni) {
    const digits = mDni[1];
    const letter = mDni[2];
    const expected = _IDENTITY_NIF_LETTERS[parseInt(digits, 10) % 23];
    if (letter !== expected) {
      return { ok: false, messageKey: IDENTITY_I18N.nifCheckLetter };
    }
    return { ok: true, normalized: nif };
  }

  const mNie = _IDENTITY_NIE_RE.exec(nif);
  if (mNie) {
    const prefix = mNie[1];
    const digits = mNie[2];
    const letter = mNie[3];
    const fullDigits = parseInt(_IDENTITY_NIE_PREFIX_MAP[prefix] + digits, 10);
    const expected = _IDENTITY_NIF_LETTERS[fullDigits % 23];
    if (letter !== expected) {
      return { ok: false, messageKey: IDENTITY_I18N.nifCheckLetter };
    }
    return { ok: true, normalized: nif };
  }

  return { ok: false, messageKey: IDENTITY_I18N.nifFormat };
}

export function isUserIdentityIntakeSatisfied(answers: Record<string, unknown>): boolean {
  return Object.keys(getUserIdentityIntakeFieldErrors(answers)).length === 0;
}

/** Field → i18n key for `useTranslation('common')`. */
export function getUserIdentityIntakeFieldErrors(
  answers: Record<string, unknown>
): Partial<Record<IdentityIntakeField, string>> {
  const out: Partial<Record<IdentityIntakeField, string>> = {};

  const fullName = String(answers.full_name ?? '').trim();
  if (fullName.length < 2) {
    out.full_name = IDENTITY_I18N.fullNameMin;
  }

  const email = String(answers.email ?? '').trim();
  if (!email) {
    out.email = IDENTITY_I18N.emailRequired;
  } else if (!isValidEmail(email)) {
    out.email = IDENTITY_I18N.emailInvalid;
  }

  const nifRaw = String(answers.nif ?? '').trim();
  if (!nifRaw) {
    out.nif = IDENTITY_I18N.nifRequired;
  } else {
    const nifResult = validateNifNie(nifRaw);
    if (nifResult.ok === false) {
      out.nif = nifResult.messageKey;
    }
  }

  return out;
}
